import { useState } from 'react';
import { motion } from 'framer-motion';
import { useInView } from '../../../hooks/useInView';

const QuoteRequestForm = () => {
  const [ref, isInView] = useInView({ threshold: 0.2 });
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    company: '',
    projectType: '',
    materialCategories: [] as string[],
    projectTimeline: '',
    estimatedBudget: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const materialOptions = [
    'Steel Building Packages',
    'Windows & Doors',
    'Kitchens & Cabinets',
    'Flooring & Tile',
    'Interior Finishes',
    'Exterior Finishes',
    'Furniture Packages',
    'Plumbing Fixtures',
    'Lighting',
    'Hardware',
  ];

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleCheckboxChange = (material: string) => {
    setFormData({
      ...formData,
      materialCategories: formData.materialCategories.includes(material)
        ? formData.materialCategories.filter((m) => m !== material)
        : [...formData.materialCategories, material],
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('fullName', formData.fullName);
      formBody.append('email', formData.email);
      formBody.append('phone', formData.phone);
      formBody.append('company', formData.company);
      formBody.append('projectType', formData.projectType);
      formBody.append('materialCategories', formData.materialCategories.join(', '));
      formBody.append('projectTimeline', formData.projectTimeline);
      formBody.append('estimatedBudget', formData.estimatedBudget);
      formBody.append('message', formData.message);

      const response = await fetch('https://readdy.ai/api/form/d6f4j0bv53dtlm7j9h00', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString(),
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          email: '',
          phone: '',
          company: '',
          projectType: '',
          materialCategories: [],
          projectTimeline: '',
          estimatedBudget: '',
          message: '',
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section ref={ref} className="py-20 lg:py-28 bg-gradient-to-b from-white to-gray-50">
      <div className="max-w-6xl mx-auto px-6 lg:px-12">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="text-center mb-12"
        >
          <h2 className="font-display font-light text-4xl md:text-5xl text-gray-900 mb-4">
            Get Your <span className="font-semibold">Free Quote</span>
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            Tell us about your project and we'll provide a detailed cost comparison showing how much you can save
          </p>
        </motion.div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left - Benefits */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-1 space-y-6"
          >
            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-time-line text-2xl text-secondary-600"></i>
              </div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">Fast Response</h3>
              <p className="text-sm text-gray-600">
                Receive your detailed quote within 24-48 hours
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-price-tag-3-line text-2xl text-secondary-600"></i>
              </div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">Transparent Pricing</h3>
              <p className="text-sm text-gray-600">
                See exactly how much you save vs. traditional suppliers
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
              <div className="w-12 h-12 bg-secondary-100 rounded-lg flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl text-secondary-600"></i>
              </div>
              <h3 className="font-semibold text-base text-gray-900 mb-2">No Obligation</h3>
              <p className="text-sm text-gray-600">
                Free consultation with no commitment required
              </p>
            </div>
          </motion.div>

          {/* Right - Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="lg:col-span-2"
          >
            <form id="homepage-quote-request" onSubmit={handleSubmit} data-readdy-form className="bg-white rounded-2xl p-8 shadow-lg border border-gray-100 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-900 mb-2">
                    Full Name *
                  </label>
                  <input
                    type="text"
                    id="fullName"
                    name="fullName"
                    value={formData.fullName}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="John Smith"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-900 mb-2">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="john@company.com"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-900 mb-2">
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="+1 (555) 123-4567"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-900 mb-2">
                    Company Name *
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors"
                    placeholder="Your Company"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="projectType" className="block text-sm font-medium text-gray-900 mb-2">
                  Project Type *
                </label>
                <select
                  id="projectType"
                  name="projectType"
                  value={formData.projectType}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                >
                  <option value="">Select project type</option>
                  <option value="Custom Home Build">Custom Home Build</option>
                  <option value="Real Estate Development">Real Estate Development</option>
                  <option value="Renovation/Remodel">Renovation/Remodel</option>
                  <option value="Commercial Construction">Commercial Construction</option>
                  <option value="Industrial Project">Industrial Project</option>
                  <option value="Multi-Family Residential">Multi-Family Residential</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-900 mb-3">
                  Material Categories Needed *
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {materialOptions.map((material) => (
                    <label key={material} className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.materialCategories.includes(material)}
                        onChange={() => handleCheckboxChange(material)}
                        className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500 cursor-pointer"
                      />
                      <span className="ml-2 text-sm text-gray-700">{material}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label htmlFor="projectTimeline" className="block text-sm font-medium text-gray-900 mb-2">
                    Project Timeline *
                  </label>
                  <select
                    id="projectTimeline"
                    name="projectTimeline"
                    value={formData.projectTimeline}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select timeline</option>
                    <option value="Immediate (0-3 months)">Immediate (0-3 months)</option>
                    <option value="Short-term (3-6 months)">Short-term (3-6 months)</option>
                    <option value="Mid-term (6-12 months)">Mid-term (6-12 months)</option>
                    <option value="Long-term (12+ months)">Long-term (12+ months)</option>
                    <option value="Planning Phase">Planning Phase</option>
                  </select>
                </div>

                <div>
                  <label htmlFor="estimatedBudget" className="block text-sm font-medium text-gray-900 mb-2">
                    Estimated Budget *
                  </label>
                  <select
                    id="estimatedBudget"
                    name="estimatedBudget"
                    value={formData.estimatedBudget}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors cursor-pointer"
                  >
                    <option value="">Select budget range</option>
                    <option value="Under $100K">Under $100K</option>
                    <option value="$100K - $500K">$100K - $500K</option>
                    <option value="$500K - $1M">$500K - $1M</option>
                    <option value="$1M - $5M">$1M - $5M</option>
                    <option value="$5M - $10M">$5M - $10M</option>
                    <option value="Over $10M">Over $10M</option>
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="message" className="block text-sm font-medium text-gray-900 mb-2">
                  Additional Details
                </label>
                <textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  maxLength={500}
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-sm text-gray-900 focus:outline-none focus:border-primary-500 transition-colors resize-none"
                  placeholder="Tell us more about your project requirements, specific materials needed, or any questions you have..."
                ></textarea>
                <div className="text-xs text-gray-500 mt-1">
                  {formData.message.length}/500 characters
                </div>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="w-full px-8 py-4 bg-gradient-to-r from-primary-600 to-primary-700 text-white text-base font-semibold rounded-full hover:shadow-lg transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
              >
                {isSubmitting ? 'Submitting...' : 'Get My Free Quote'}
                {!isSubmitting && <i className="ri-arrow-right-line ml-2"></i>}
              </button>

              {submitStatus === 'success' && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg text-sm text-green-800">
                  <i className="ri-check-line mr-2"></i>
                  Thank you! We'll send your detailed quote within 24-48 hours.
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800">
                  <i className="ri-error-warning-line mr-2"></i>
                  Something went wrong. Please try again or contact us directly.
                </div>
              )}
            </form>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default QuoteRequestForm;